import { useState, useMemo, useEffect, useDeferredValue } from 'react'
import { useNavigate } from 'react-router-dom'
import { PackageService } from '../services/packageService'
import { rankPackages } from '../utils/packageSearch'
import { Package } from '../config/api'
import { useBlogs } from './useBlogs'

interface SearchResult {
  id: string
  title: string
  content: string
  type: 'page' | 'package' | 'service' | 'blog'
  url: string
  section?: string
}

// Search index for all website content
const searchIndex: SearchResult[] = [
  // Pages
  {
    id: 'home',
    title: 'Home - Memento Travel Co.',
    content: 'travel packages destinations flights accommodation booking seaside getaway vacation holiday trips',
    type: 'page',
    url: '/'
  },
  {
    id: 'about',
    title: 'About Us',
    content: 'about memento travel company story team experience global adventures cruise industry veteran rene traut wanderlust travel dreams',
    type: 'page',
    url: '/about'
  },
  {
    id: 'packages',
    title: 'Travel Packages',
    content: 'travel packages destinations holidays trips vacation booking accommodation flights international domestic featured packages',
    type: 'page',
    url: '/packages'
  },
  {
    id: 'contact',
    title: 'Contact Us',
    content: 'contact us get in touch phone email address business hours booking inquiries travel planning',
    type: 'page',
    url: '/contact'
  },
  {
    id: 'services',
    title: 'Our Services',
    content: 'travel services flights booking day trips excursions travel insurance protection coverage',
    type: 'page',
    url: '/services'
  },

  // Services
  {
    id: 'flights',
    title: 'Flights',
    content: 'flight booking domestic international airlines tickets reservations travel arrangements',
    type: 'service',
    url: '/services#flights',
    section: 'Services'
  },
  {
    id: 'day-trips',
    title: 'Day Trips',
    content: 'day trips excursions local tours sightseeing activities guided tours adventure experiences',
    type: 'service',
    url: '/services#day-trips',
    section: 'Services'
  },
  {
    id: 'travel-insurance',
    title: 'Travel Insurance',
    content: 'travel insurance protection coverage medical emergency trip cancellation baggage protection safety',
    type: 'service',
    url: '/services#travel-insurance',
    section: 'Services'
  },

  // Common destinations and keywords
  {
    id: 'destinations-popular',
    title: 'Popular Destinations',
    content: 'greece italy spain france portugal turkey morocco egypt dubai thailand bali maldives japan singapore malaysia vietnam cambodia laos indonesia philippines',
    type: 'package',
    url: '/packages'
  },
  {
    id: 'destinations-europe',
    title: 'European Destinations',
    content: 'europe european travel paris london rome amsterdam berlin vienna prague budapest barcelona madrid lisbon stockholm copenhagen',
    type: 'package',
    url: '/packages'
  },
  {
    id: 'destinations-asia',
    title: 'Asian Destinations',
    content: 'asia asian travel tokyo kyoto seoul hong kong shanghai beijing mumbai delhi kolkata bangkok phuket kuala lumpur singapore jakarta',
    type: 'package',
    url: '/packages'
  },
  {
    id: 'destinations-africa',
    title: 'African Destinations',
    content: 'africa african travel cairo marrakech casablanca cape town johannesburg nairobi dar es salaam mauritius seychelles zanzibar',
    type: 'package',
    url: '/packages'
  },

  // Travel types
  {
    id: 'honeymoon',
    title: 'Honeymoon Packages',
    content: 'honeymoon romantic getaway couples travel romance luxury accommodation private tours intimate dining experiences',
    type: 'package',
    url: '/packages'
  },
  {
    id: 'family-travel',
    title: 'Family Travel',
    content: 'family travel kids children family-friendly accommodation activities theme parks resorts entertainment family vacation',
    type: 'package',
    url: '/packages'
  },
  {
    id: 'adventure-travel',
    title: 'Adventure Travel',
    content: 'adventure travel hiking trekking climbing safari diving snorkeling extreme sports outdoor activities adrenaline',
    type: 'package',
    url: '/packages'
  },
  {
    id: 'luxury-travel',
    title: 'Luxury Travel',
    content: 'luxury travel premium accommodation five star hotels resorts first class business class private jet yacht charter',
    type: 'package',
    url: '/packages'
  },
  {
    id: 'budget-travel',
    title: 'Budget Travel',
    content: 'budget travel affordable cheap accommodation hostels backpacking economy flights package deals discounts',
    type: 'package',
    url: '/packages'
  },

  // Common travel terms
  {
    id: 'booking',
    title: 'Booking & Reservations',
    content: 'booking reservation inquiry quote customize trip planning itinerary schedule dates availability confirmation',
    type: 'page',
    url: '/contact'
  },
  {
    id: 'reviews-testimonials',
    title: 'Reviews & Testimonials',
    content: 'reviews testimonials customer feedback experiences recommendations satisfied clients happy customers excellent service',
    type: 'page',
    url: '/#reviews'
  }
]

export function useSearch() {
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const navigate = useNavigate()
  const { blogs = [] } = useBlogs()

  // Lazy-load the FULL package catalog the first time search is opened, so the
  // navbar search covers every package (not just the first page) without
  // fetching anything on regular page loads. The service caches the catalog.
  const [catalog, setCatalog] = useState<Package[]>([])
  useEffect(() => {
    if (!isSearchOpen || catalog.length) return
    let cancelled = false
    PackageService.getInstance()
      .getSearchCatalog()
      .then(packages => {
        if (!cancelled) setCatalog(packages)
      })
      .catch(() => {
        // Degrade gracefully to static + blog results
      })
    return () => {
      cancelled = true
    }
  }, [isSearchOpen, catalog.length])

  // Defer ranking so fast typing stays smooth (ranking ~450 packages takes ~100ms)
  const deferredQuery = useDeferredValue(searchQuery)

  const searchResults = useMemo(() => {
    if (!deferredQuery.trim()) return []

    const query = deferredQuery.toLowerCase().trim()

    // Full-text rank the whole catalog (name, intro, description, destination)
    // with typo tolerance - keep the most relevant packages
    const packageResults: SearchResult[] = rankPackages(catalog, deferredQuery)
      .slice(0, 6)
      .map(pkg => ({
        id: `package-${pkg.id}`,
        title: pkg.name || 'Travel Package',
        content: `${pkg.intro || pkg.description || ''} ${pkg.location || ''} ${pkg.destination?.name || ''} travel package vacation holiday`,
        type: 'package' as const,
        url: `/packages/${pkg.slug}`,
        section: 'Travel Packages'
      }))

    // Create dynamic search results from blogs
    const blogResults: SearchResult[] = blogs.map(blog => ({
      id: `blog-${blog.id}`,
      title: blog.title || 'Blog Post',
      content: `${blog.excerpt || blog.description || ''} blog post article travel tips advice`,
      type: 'blog' as const,
      url: `/blog/${blog.slug}`,
      section: 'Blog'
    }))

    // Packages are already relevance-ranked (with typo tolerance) by rankPackages,
    // so they skip the substring filter - score them by rank to preserve order
    const scoredPackages = packageResults.map((item, index) => ({
      ...item,
      score: 10 - index,
    }))

    // Static pages and blogs keep the original substring matching and scoring
    const scoredOthers = [...searchIndex, ...blogResults]
      .filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query)
      )
      .map(item => {
        // Calculate relevance score
        let score = 0
        if (item.title.toLowerCase().includes(query)) score += 3
        if (item.content.toLowerCase().includes(query)) score += 1

        // Boost exact matches
        if (item.title.toLowerCase() === query) score += 5
        if (item.content.toLowerCase().split(' ').includes(query)) score += 2

        // Boost recent blogs
        if (item.type === 'blog') score += 1

        return { ...item, score }
      })

    return [...scoredPackages, ...scoredOthers]
      .sort((a, b) => b.score - a.score)
      .slice(0, 8) // Limit to top 8 results
  }, [deferredQuery, catalog, blogs])

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  const handleResultClick = (result: SearchResult) => {
    setIsSearchOpen(false)
    setSearchQuery('')

    if (result.url.includes('#')) {
      // Handle anchor links
      const [path, anchor] = result.url.split('#')
      navigate(path === '' ? '/' : path)
      setTimeout(() => {
        const element = document.getElementById(anchor)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    } else {
      navigate(result.url)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setIsSearchOpen(false)
  }

  return {
    searchQuery,
    searchResults,
    isSearchOpen,
    setIsSearchOpen,
    handleSearch,
    handleResultClick,
    clearSearch
  }
}